'use client';

import Link from 'next/link';
import { normalizePath, resolveHref } from './navigation.utils';
import type { CtaItemDto, NavItemDto } from '@/lib/sanity/dto';
import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';

type BaseLinkProps = {
    item: NavItemDto | CtaItemDto | null;
    className?: string;
    children?: ReactNode;
    ariaCurrent?: 'page' | 'true';
    scroll?: boolean;
    ariaHaspopup?: 'dialog';
};

export function BaseLink({ item, className, children, ariaCurrent, scroll, ariaHaspopup }: BaseLinkProps) {
    const pathname = normalizePath(usePathname() ?? '/');

    if (!item) return null;

    const href = resolveHref(item);
    const label = children ?? item.label;

    if (item.linkType === 'external') {
        return (
            <a href={href} className={className} target="_blank" rel="noopener noreferrer" aria-current={ariaCurrent}>
                {label}
            </a>
        );
    }

    if (href.includes('#')) {
        const [pathPart, hashPart] = href.split('#');
        const targetPath = normalizePath(pathPart || '/');
        const isSamePage = targetPath === pathname;

        if (isSamePage && hashPart) {
            return (
                <a
                    href={`#${hashPart}`}
                    className={className}
                    aria-current={ariaCurrent}
                    aria-haspopup={ariaHaspopup}
                    onClick={(e) => {
                        e.preventDefault();

                        const el = document.getElementById(hashPart);
                        if (!el) return;

                        history.replaceState(null, '', `#${hashPart}`);

                        el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }}
                >
                    {label}
                </a>
            );
        }
    }

    return (
        <Link href={href} className={className} aria-current={ariaCurrent} aria-haspopup={ariaHaspopup} scroll={scroll}>
            {label}
        </Link>
    );
}
